import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getSetOrRemoveChargePointsLocationDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/set-or-remove-charge-points-location.form-default-values';
import { SetOrRemoveChargePointsLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/set-or-remove-charge-points-location.model';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import {
  useCpmsBoRemoveChargePointsLocationMutation,
  useCpmsBoSetChargePointsLocationMutation,
} from '@app/core/service/cpms-bo-charge-point.service';

import {
  UpdateChargePointLocationContextProvider,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointLocationUpdateFormController } from './charge-point-location-update-form/charge-point-location-update-form.controller';

interface ChargePointLocationUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointLocationUpdateController = ({
  onCancel,
  onSuccess,
}: ChargePointLocationUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const [setChargePointsLocationMutation] =
    useCpmsBoSetChargePointsLocationMutation();
  const [removeChargePointsLocationMutation] =
    useCpmsBoRemoveChargePointsLocationMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    SetOrRemoveChargePointsLocationModel
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-location-update.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    SetOrRemoveChargePointsLocationModel
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (sanitizedObject.locationId) {
      setChargePointsLocationMutation({
        chargePointIds: fieldValues.chargePointIds,
        locationId: sanitizedObject.locationId,
      })
        .unwrap()
        .then(() => {
          toast.success(t('$.charge-point-location-update.success'));
          onSuccess();
        })
        .catch(({ data: { message: errorMessage } }) => {
          toast.error(
            t('$.charge-point-location-update.error', {
              error: errorMessage,
            }),
          );
        })
        .finally(() => setMultiStepFormState(MultiStepFormState.Default));
    } else {
      removeChargePointsLocationMutation({
        chargePointIds: fieldValues.chargePointIds,
      })
        .unwrap()
        .then(() => {
          toast.success(t('$.charge-point-location-update.success'));
          onSuccess();
        })
        .catch(({ data: { message: errorMessage } }) => {
          toast.error(
            t('$.charge-point-location-update.error', {
              error: errorMessage,
            }),
          );
        })
        .finally(() => setMultiStepFormState(MultiStepFormState.Default));
    }
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getSetOrRemoveChargePointsLocationDefaultValues(chargePoint);

  return (
    <UpdateChargePointLocationContextProvider data={data}>
      <ChargePointLocationUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateChargePointLocationContextProvider>
  );
};
